import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, DialogTitle, DialogTitleProps, IconButton, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import useViewportMode, { ViewportMode } from '../../hooks/useViewportMode';

export interface ResponsiveDialogHeaderProps extends Omit<DialogTitleProps, 'title'> {
    title: ReactNode;
    subtitle?: ReactNode;
    actions?: ReactNode;
    onClose?: () => void;
    closeLabel?: string;
    viewportOverride?: ViewportMode;
}

const ResponsiveDialogHeader = ({
    title,
    subtitle,
    actions,
    onClose,
    closeLabel = 'Close',
    viewportOverride,
    sx,
    ...titleProps
}: ResponsiveDialogHeaderProps) => {
    const viewport = useViewportMode();
    const mode = viewportOverride ?? viewport.mode;
    const isPhone = mode === 'phone';
    const isTouchLayout = isPhone || mode === 'tablet';

    return (
        <DialogTitle
            {...titleProps}
            data-testid="responsive-dialog-header"
            sx={{
                flex: '0 0 auto',
                px: isPhone ? 2 : 3,
                py: isPhone ? 1.5 : 3,
                ...(isPhone && {
                    paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
                    minHeight: 56,
                }),
                ...(isTouchLayout && { '& .MuiButtonBase-root': { minWidth: 44, minHeight: 44 } }),
                ...sx,
                ...(isPhone && {
                    paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
                }),
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        component="div"
                        variant={isPhone ? 'h6' : 'h5'}
                        sx={{ overflowWrap: 'anywhere', lineHeight: 1.25 }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {actions && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto' }}>
                        {actions}
                    </Stack>
                )}
                {onClose && (
                    <IconButton
                        aria-label={closeLabel}
                        edge="end"
                        onClick={onClose}
                        sx={{ width: 44, height: 44, flex: '0 0 auto' }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                )}
            </Stack>
        </DialogTitle>
    );
};

export default ResponsiveDialogHeader;
